import React, { useState } from 'react';
import { SearchIcon, BookOpenIcon, AlertCircleIcon, CheckCircleIcon, XCircleIcon, MonitorIcon, UploadIcon, Target } from 'lucide-react';
import { AnalyticsDashboard } from './AnalyticsDashboard';
import LearningCenter from '../Learning/LearningCenter';
import { getApiRootUrl } from '@/services/api';
import Modal from '@/components/common/Modal';

interface SimilarIncident {
  incident_number: string;
  cleaned_title: string;
  problem_id: string;
  similarity_score: number;
  cluster_id: number;
}

interface PlaybookRecommendation {
  cluster_id: number;
  cluster_name: string;
  cluster_size: number;
  problem_ids: string[];
  playbook_file: string;
}

interface AnalysisResponse {
  cleaned_issue: string;
  embedding_text: string;
  similar_incidents: SimilarIncident[];
  recommended_playbook: PlaybookRecommendation | null;
  confidence_score: number;
  incident_exists: boolean;
  incident_number: string | null;
}

export const TicketAnalysisPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('analysis');
  const [analysis, setAnalysis] = useState<AnalysisResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ticketJson, setTicketJson] = useState('');
  const [progress, setProgress] = useState<{ step: string; progress: number } | null>(null);
  const [modal, setModal] = useState<{
    open: boolean;
    variant: 'success' | 'error' | 'info';
    title?: string;
    message: string;
    details?: string[];
  }>({ open: false, variant: 'info', message: '' });

  const handleAnalyze = async () => {
    if (!ticketJson) return;

    setLoading(true);
    setError(null);
    setAnalysis(null);

    // Simulate analysis steps
    const steps = [
      { msg: "Parsing and validating ticket data...", prog: 10 },
      { msg: "Generating vector embeddings via OpenAI...", prog: 35 },
      { msg: "Searching vector database for similar incidents...", prog: 60 },
      { msg: "Retrieving recommended playbooks...", prog: 85 },
      { msg: "Finalizing analysis...", prog: 95 }
    ];

    for (const step of steps) {
      setProgress({ step: step.msg, progress: step.prog });
      await new Promise(r => setTimeout(r, 800)); // Simulated delay
    }

    try {
      const response = await fetch(`${getApiRootUrl()}/servicenow/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket_json: JSON.parse(ticketJson) }),
      });

      if (!response.ok) throw new Error('Analysis failed');

      const data = await response.json();
      setAnalysis(data);
      setProgress(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setProgress(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadPlaybook = async (clusterId: number) => {
    try {
      const response = await fetch(`${getApiRootUrl()}/servicenow/playbook/${clusterId}`);
      if (!response.ok) {
        throw new Error('Failed to load playbook');
      }
      const data = await response.json();
      setPlaybook(data.content);
    } catch (err) {
      setError('Failed to load playbook');
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${getApiRootUrl()}/servicenow/parse-pdf`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to parse PDF');
      }

      const data = await response.json();
      setTicketJson(JSON.stringify(data, null, 2));
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to parse PDF');
    } finally {
      setLoading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const [expandedIncident, setExpandedIncident] = useState<string | null>(null);
  const [incidentDetails, setIncidentDetails] = useState<Record<string, any>>({});
  const [detailsLoading, setDetailsLoading] = useState<string | null>(null);
  const [playbook, setPlaybook] = useState<string | null>(null);

  const handleExpandIncident = async (incidentNumber: string) => {
    if (expandedIncident === incidentNumber) {
      setExpandedIncident(null);
      return;
    }

    setExpandedIncident(incidentNumber);

    if (!incidentDetails[incidentNumber]) {
      try {
        setDetailsLoading(incidentNumber);
        const response = await fetch(`${getApiRootUrl()}/servicenow/incident/${incidentNumber}`);
        if (response.ok) {
          const data = await response.json();
          setIncidentDetails(prev => ({ ...prev, [incidentNumber]: data }));
        }
      } catch (error) {
        console.error('Failed to fetch incident details:', error);
      } finally {
        setDetailsLoading(null);
      }
    }
  };

  const [saveProgress, setSaveProgress] = useState<{ step: string; progress: number } | null>(null);

  const handleSaveIncident = async () => {
    if (!analysis || !ticketJson) return;

    try {
      setLoading(true);

      // Step 1: Extraction
      setSaveProgress({ step: "Extracting and normalizing incident data...", progress: 10 });
      await new Promise(resolve => setTimeout(resolve, 800));

      // Step 2: Embedding
      setSaveProgress({ step: "Generating vector embeddings via OpenAI...", progress: 40 });
      await new Promise(resolve => setTimeout(resolve, 1200));

      // Step 3: Similarity
      setSaveProgress({ step: "Calculating semantic similarity with existing nodes...", progress: 70 });
      await new Promise(resolve => setTimeout(resolve, 800));

      // Step 4: Clustering
      setSaveProgress({ step: "Identifying optimal cluster assignment...", progress: 90 });
      await new Promise(resolve => setTimeout(resolve, 600));

      const ticketData = JSON.parse(ticketJson);

      const response = await fetch(`${getApiRootUrl()}/servicenow/save-incident`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket_json: ticketData })
      });

      if (!response.ok) {
        throw new Error('Failed to save incident');
      }

      const result = await response.json();

      setSaveProgress({ step: "Complete!", progress: 100 });
      await new Promise(resolve => setTimeout(resolve, 500));

      setModal({
        open: true,
        variant: 'success',
        title: 'Incident Saved',
        message: 'Incident has been saved and clustered successfully.',
        details: [
          `Incident: ${result.incident_number}`,
          `Cluster: ${result.cluster_id} (${result.cluster_action})`,
          `Similarity: ${(result.similarity_to_cluster * 100).toFixed(1)}%`
        ]
      });

      // Refresh analysis to show the new state (button hidden)
      handleAnalyze();

    } catch (err: any) {
      setError(err.message || 'Failed to save incident');
    } finally {
      setLoading(false);
      setSaveProgress(null);
    }
  };

  const renderMarkdown = (md: string) => {
    const lines = md.split('\n');
    const elements: JSX.Element[] = [];
    let currentList: string[] = [];

    const flushList = () => {
      if (currentList.length > 0) {
        elements.push(
          <ul key={`list-${elements.length}`} className="list-disc pl-6 mb-4 space-y-1">
            {currentList.map((item, i) => (
              <li key={i} className="text-gray-700" dangerouslySetInnerHTML={{ __html: formatInline(item) }} />
            ))}
          </ul>
        );
        currentList = [];
      }
    };

    const formatInline = (text: string): string => {
      // Handle bold text **text**
      text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      return text;
    };

    lines.forEach((line, idx) => {
      // Headers
      if (line.startsWith('# ')) {
        flushList();
        elements.push(<h1 key={idx} className="text-2xl font-bold mb-4 mt-6">{line.slice(2)}</h1>);
      } else if (line.startsWith('## ')) {
        flushList();
        elements.push(<h2 key={idx} className="text-xl font-semibold mb-3 mt-5 text-blue-900">{line.slice(3)}</h2>);
      } else if (line.startsWith('### ')) {
        flushList();
        elements.push(<h3 key={idx} className="text-lg font-medium mb-2 mt-4 text-gray-800">{line.slice(4)}</h3>);
      }
      // Horizontal rule
      else if (line === '---') {
        flushList();
        elements.push(<hr key={idx} className="my-4 border-gray-300" />);
      }
      // List items (including ** prefixed ones)
      else if (line.match(/^[\s]*[-*•]\s/) || line.match(/^[\s]*\*\*[-*•]/)) {
        const cleaned = line.replace(/^[\s]*[-*•]\s/, '').replace(/^[\s]*\*\*[-*•]/, '');
        currentList.push(cleaned);
      }
      // Numbered lists
      else if (line.match(/^[\s]*\d+\.\s/)) {
        flushList();
        const text = line.replace(/^[\s]*\d+\.\s/, '');
        const num = line.match(/^\d+/)?.[0];
        elements.push(
          <p key={idx} className="mb-2 pl-4">
            <span className="font-medium">{num}.</span> <span dangerouslySetInnerHTML={{ __html: formatInline(text) }} />
          </p>
        );
      }
      // Regular paragraph
      else if (line.trim()) {
        flushList();
        elements.push(<p key={idx} className="mb-3 text-gray-700" dangerouslySetInnerHTML={{ __html: formatInline(line) }} />);
      }
      // Empty line
      else {
        flushList();
      }
    });

    flushList();
    return elements;
  };

  const loadSampleTicket = () => {
    const sample = {
      "pdf_fields": {
        "Short description": "Vision Manual Correction : Finance posting errors--Order:5045282854/801055779 site:TXC 16-Order Incorrect",
        "Description": "POS STATUS = FINALIZED; ORDER TYPE = STORE_ORDER; GK GROSS SALES = $ 102.46;"
      },
      "incident_section": {
        "Number": "SAMPLE001",
        "Incident state": "New",
        "Category": "Software",
        "Subcategory": "Error Condition",
        "Priority": "2 - High"
      }
    };
    setTicketJson(JSON.stringify(sample, null, 2));
  };

  const getConfidenceColor = (score: number) => {
    if (score >= 0.9) return 'text-green-600';
    if (score >= 0.75) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <MonitorIcon className="w-8 h-8 text-blue-600" />
                ServiceNow Ticket Analysis
              </h1>
              <p className="text-gray-600 mt-1">
                Paste ServiceNow ticket JSON to find similar incidents and get playbook recommendations
              </p>
            </div>
            <button
              onClick={loadSampleTicket}
              className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
            >
              Load Sample
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-6 w-fit">
          <button
            onClick={() => setActiveTab('analysis')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'analysis'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
              }`}
          >
            Ticket Analysis
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'analytics'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
              }`}
          >
            Analytics Dashboard
          </button>
          <button
            onClick={() => setActiveTab('learning')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'learning'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
              }`}
          >
            Learning Center
          </button>
          <button
            onClick={() => setActiveTab('docs')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'docs'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
              }`}
          >
            Documentation
          </button>
        </div>

        {activeTab === 'analytics' ? (
          <AnalyticsDashboard />
        ) : activeTab === 'learning' ? (
          <LearningCenter />
        ) : activeTab === 'docs' ? (
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Project Documentation</h2>
              <a
                href="/nexus_presentation.md"
                download
                className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <UploadIcon className="w-4 h-4 rotate-180" />
                Download Presentation
              </a>
            </div>
            <div className="prose max-w-none">
              <div className="bg-gray-50 p-8 rounded-xl border border-gray-200">
                {renderMarkdown(`
# **NEXUS**
## AI-Powered Incident Resolution

**Current Status:** POC Complete
**Focus:** Intelligent Ticket Analysis & Playbook Automation

---

## 🚀 What We Have Achieved

1.  **Intelligent Parsing**:
    *   Automated PDF & JSON parsing of ServiceNow tickets.
    *   Extracting key signals from unstructured text.

2.  **Smart Clustering**:
    *   Grouped **82+ incidents** into meaningful clusters.
    *   Identified top recurring issues (e.g., *DT Finance Posting Errors*).

3.  **Playbook Recommendations**:
    *   Auto-matching incoming tickets to existing solutions (85%+ confidence).
    *   **Auto-generation** of playbooks for new incident types.

4.  **Analytics Dashboard**:
    *   Real-time visualization of ticket categories, priorities, and trends.

---

## 🔮 Next Steps: Scaling Up

**From POC to Production**

*   **Scale Data Ingestion**:
    *   Ingest **thousands of historical tickets** to improve cluster accuracy.
    *   Refine matching algorithms to reduce false positives (< 5%).

*   **Real-time Webhooks**:
    *   Integrate with ServiceNow webhooks to analyze tickets instantly upon creation.
    *   Proactive alerting for "Major Incident" candidates.

---

## 🤖 The Verify Agent

**Goal:** Move from *Recommendation* to *Action*

*   **Real-time Verification**:
    *   Agent will not just suggest a fix, but **verify system state**.
    *   *Example:* "Check if Order #123 is stuck in Hybris."

*   **API Integration**:
    *   Fetch live data from SAP, Hybris, and internal APIs.
    *   Compare actual system state against "Expected State" definitions.

*   **Human-in-the-Loop**:
    *   Agent proposes a fix -> Human approves -> Agent executes & verifies.
                `)}
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Input */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold mb-4">Ticket Input</h2>

              {progress && (
                <div className="mb-8 bg-white p-6 rounded-lg shadow-sm border border-blue-100">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Analysis in Progress</h3>
                    <span className="text-blue-600 font-medium">{progress.progress}%</span>
                  </div>

                  {/* Graphical Stepper */}
                  <div className="relative">
                    <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-blue-100">
                      <div
                        style={{ width: `${progress.progress}%` }}
                        className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-600 transition-all duration-500 ease-out"
                      ></div>
                    </div>

                    <div className="grid grid-cols-5 gap-2 text-xs text-center">
                      {['Parsing', 'Embedding', 'Vector Search', 'Playbooks', 'Finalizing'].map((step, idx) => {
                        const isActive = progress.progress >= (idx + 1) * 20;
                        return (
                          <div key={idx} className={`flex flex-col items-center ${isActive ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>
                            <div className={`w-3 h-3 rounded-full mb-1 ${isActive ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
                            <span>{step}</span>
                          </div>
                        );
                      })}
                    </div>

                    <p className="text-center text-sm text-gray-600 mt-4 font-medium animate-pulse">
                      {progress.step}
                    </p>
                  </div>
                </div>
              )}

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Ticket PDF
                </label>
                <div className="flex items-center gap-2">
                  <label className={`flex-1 cursor-pointer bg-white border border-dashed border-gray-300 rounded-lg px-4 py-8 hover:bg-gray-50 hover:border-blue-400 transition-all flex flex-col items-center justify-center gap-2 text-gray-600 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <div className="p-3 bg-blue-50 rounded-full text-blue-600">
                      <UploadIcon className="w-6 h-6" />
                    </div>
                    <div className="text-center">
                      <span className="font-medium text-blue-600">Click to upload PDF</span>
                      <span className="text-gray-400 block text-xs mt-1">or drag and drop</span>
                    </div>
                    <input
                      type="file"
                      accept=".pdf"
                      className="hidden"
                      onChange={handleFileUpload}
                      disabled={loading}
                    />
                  </label>
                </div>
              </div>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 bg-white text-gray-500 font-medium">OR PASTE JSON</span>
                </div>
              </div>

              <textarea
                className="w-full h-96 p-4 border border-gray-300 rounded-lg font-mono text-sm"
                placeholder='Paste ServiceNow ticket JSON here...\n\nExample:\n{\n  "pdf_fields": {\n    "Short description": "Finance posting error..."\n  },\n  "incident_section": {\n    "Category": "Software",\n    "Subcategory": "Error Condition"\n  }\n}'
                value={ticketJson}
                onChange={(e) => setTicketJson(e.target.value)}
              />

              <button
                onClick={handleAnalyze}
                disabled={loading || !ticketJson}
                className="mt-4 w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading && !progress ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <SearchIcon className="w-5 h-5" />
                    Analyze Ticket
                  </>
                )}
              </button>

              {error && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <XCircleIcon className="w-5 h-5 text-red-600 mt-0.5" />
                  <div>
                    <p className="text-red-800 font-medium">Error</p>
                    <p className="text-red-600 text-sm">{error}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Right: Results */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold mb-4">Analysis Results</h2>

              {!analysis && !loading && (
                <div className="text-center py-12 text-gray-500">
                  <SearchIcon className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>Paste a ticket and click "Analyze" to see results</p>
                </div>
              )}

              {analysis && (
                <div className="space-y-6">
                  {/* Confidence Score */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Confidence Score</span>
                      <span className={`text-2xl font-bold ${getConfidenceColor(analysis.confidence_score)}`}>
                        {(analysis.confidence_score * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${analysis.confidence_score >= 0.9 ? 'bg-green-600' :
                          analysis.confidence_score >= 0.75 ? 'bg-yellow-600' : 'bg-red-600'
                          }`}
                        style={{ width: `${analysis.confidence_score * 100}%` }}
                      />
                    </div>
                  </div>

                  {/* Cleaned Issue */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Cleaned Issue</h3>
                    <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded">{analysis.cleaned_issue}</p>
                  </div>

                  {/* Similar Incidents */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">
                      Similar Incidents ({analysis.similar_incidents.length})
                    </h3>
                    <div className="space-y-2">
                      {analysis.similar_incidents.map((incident, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                          <div
                            className="p-3 cursor-pointer hover:bg-gray-100 transition-colors flex justify-between items-start"
                            onClick={() => handleExpandIncident(incident.incident_number)}
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-mono text-sm font-medium text-blue-600">
                                  {incident.incident_number}
                                </span>
                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${incident.similarity_score >= 0.9 ? 'bg-green-100 text-green-800' :
                                  incident.similarity_score >= 0.75 ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-red-100 text-red-800'
                                  }`}>
                                  {(incident.similarity_score * 100).toFixed(1)}% Match
                                </span>
                              </div>
                              <p className="text-xs text-gray-600 line-clamp-2">{incident.cleaned_title}</p>
                            </div>
                            <div className="ml-2">
                              {expandedIncident === incident.incident_number ? (
                                <div className="text-gray-400">▼</div>
                              ) : (
                                <div className="text-gray-400">▶</div>
                              )}
                            </div>
                          </div>

                          {expandedIncident === incident.incident_number && (
                            <div className="p-3 bg-white border-t border-gray-200 text-sm">
                              {detailsLoading === incident.incident_number ? (
                                <div className="flex justify-center py-2">
                                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                </div>
                              ) : incidentDetails[incident.incident_number] ? (
                                <div className="space-y-3">
                                  <div>
                                    <span className="font-medium text-gray-700 block mb-1">Description:</span>
                                    <p className="text-gray-600 bg-gray-50 p-2 rounded text-xs whitespace-pre-wrap">
                                      {incidentDetails[incident.incident_number].description}
                                    </p>
                                  </div>
                                  <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div>
                                      <span className="font-medium text-gray-700">State:</span>
                                      <span className="ml-1 text-gray-600">{incidentDetails[incident.incident_number].state}</span>
                                    </div>
                                    <div>
                                      <span className="font-medium text-gray-700">Priority:</span>
                                      <span className="ml-1 text-gray-600">{incidentDetails[incident.incident_number].priority}</span>
                                    </div>
                                    <div>
                                      <span className="font-medium text-gray-700">Created:</span>
                                      <span className="ml-1 text-gray-600">{incidentDetails[incident.incident_number].created_on?.split(' ')[0]}</span>
                                    </div>
                                    <div>
                                      <span className="font-medium text-gray-700">Closed:</span>
                                      <span className="ml-1 text-gray-600">{incidentDetails[incident.incident_number].closed_on?.split(' ')[0]}</span>
                                    </div>
                                  </div>
                                  {incidentDetails[incident.incident_number].resolution_notes && (
                                    <div>
                                      <span className="font-medium text-green-700 block mb-1">Resolution:</span>
                                      <p className="text-gray-600 bg-green-50 p-2 rounded text-xs whitespace-pre-wrap border border-green-100">
                                        {incidentDetails[incident.incident_number].resolution_notes}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <p className="text-red-500 text-xs">Failed to load details</p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Recommended Playbook */}
                  {analysis.recommended_playbook && (
                    <div className="border-t pt-4">
                      <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                        <BookOpenIcon className="w-5 h-5 text-green-600" />
                        Recommended Playbook
                      </h3>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h4 className="font-medium text-green-900 mb-2">
                          {analysis.recommended_playbook.cluster_name}
                        </h4>
                        <div className="space-y-1 text-sm text-green-800 mb-3">
                          <p>• {analysis.recommended_playbook.cluster_size} similar incidents</p>
                          <p>• Problem IDs: {analysis.recommended_playbook.problem_ids.join(', ')}</p>
                        </div>
                        {analysis.incident_exists ? (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                            <p className="text-sm text-blue-800">
                              ✓ Incident <strong>{analysis.incident_number}</strong> already exists in database
                            </p>
                          </div>
                        ) : null}

                        {progress && (
                          <div className="mb-4">
                            <div className="flex justify-between text-xs font-medium text-gray-600 mb-1">
                              <span>{progress.step}</span>
                              <span>{progress.progress}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                                style={{ width: `${progress.progress}%` }}
                              />
                            </div>
                          </div>
                        )}

                        <div className={analysis.incident_exists ? "grid grid-cols-1 gap-2" : "grid grid-cols-2 gap-2"}>
                          <button
                            onClick={() => handleLoadPlaybook(analysis.recommended_playbook!.cluster_id)}
                            className={`px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 ${analysis.incident_exists ? 'w-full' : ''}`}
                          >
                            View Playbook
                          </button>
                          {!analysis.incident_exists && (
                            <button
                              onClick={handleSaveIncident}
                              disabled={loading}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
                            >
                              {loading && !progress ? 'Saving...' : 'Save to DB'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Playbook Modal */}
        {playbook && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-50">
            <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="flex justify-between items-center p-6 border-b">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <BookOpenIcon className="w-6 h-6 text-green-600" />
                  Playbook Details
                </h2>
                <button
                  onClick={() => setPlaybook(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XCircleIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="flex flex-1 overflow-hidden">
                {/* Main Content */}
                <div className="flex-1 p-8 overflow-y-auto border-r border-gray-200">
                  <div className="prose max-w-none">
                    {renderMarkdown(playbook)}
                  </div>
                </div>

                {/* Verification Sidebar */}
                <div className="w-80 bg-gray-50 p-6 overflow-y-auto">
                  <div className="sticky top-0">
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                      <Target className="w-4 h-4 text-blue-600" />
                      Verification Data
                    </h3>

                    <div className="space-y-4">
                      <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                        <span className="text-xs font-medium text-gray-500 block mb-1">Source System</span>
                        <span className="text-sm font-semibold text-blue-700">SAP Hybris</span>
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <span className="text-xs font-medium text-gray-500 block mb-1">Required Data</span>
                          <code className="text-xs bg-gray-100 p-1 rounded block">Order Status</code>
                          <code className="text-xs bg-gray-100 p-1 rounded block mt-1">Consignment Status</code>
                        </div>
                      </div>

                      <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                        <span className="text-xs font-medium text-gray-500 block mb-1">Source System</span>
                        <span className="text-sm font-semibold text-green-700">SAP Finance</span>
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <span className="text-xs font-medium text-gray-500 block mb-1">Required Data</span>
                          <code className="text-xs bg-gray-100 p-1 rounded block">Payment Log</code>
                          <code className="text-xs bg-gray-100 p-1 rounded block mt-1">Posting Date</code>
                        </div>
                      </div>

                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mt-6">
                        <h4 className="text-sm font-bold text-blue-900 mb-2 flex items-center gap-2">
                          <MonitorIcon className="w-4 h-4" />
                          Verify Agent
                        </h4>
                        <p className="text-xs text-blue-700 mb-3">
                          The Verify Agent will use these data points to validate the fix in real-time.
                        </p>
                        <button className="w-full py-2 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-colors">
                          Run Verification (Mock)
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <Modal
        open={modal.open}
        onClose={() => setModal(m => ({ ...m, open: false }))}
        variant={modal.variant}
        title={modal.title}
        message={modal.message}
        details={modal.details}
      />
    </div>
  );
};
